/**
 * Videos: ansehen, was drin ist, und in Stücken hochladen.
 *
 * Der Server kann von einem Video nichts wissen — ohne ffmpeg kann er es
 * nicht öffnen, nicht messen, nicht umwandeln und kein Standbild daraus
 * ziehen. Also macht es der Browser, der die Datei ohnehin in der Hand hat:
 * er liest Dauer und Maße, zeichnet den ersten Augenblick auf eine Leinwand
 * und schiebt die Datei stückweise hinüber.
 *
 * **Umgewandelt wird seit #4 doch**, und zwar hier vor dem ersten Byte:
 * `verkleinereVideo()` rechnet auf die eingestellte Kantenlänge und Bildrate
 * herunter und schreibt H.264. Hier stand vorher, der einzige Hebel liege
 * beim Absender — wer beim Teilen *„Am kompatibelsten"* wähle, bekomme
 * H.264 —, und genau das war der Anlass: „damit man nicht auf Einstellungen
 * der Foto-App im Export angewiesen ist."
 *
 * Der Satz gilt weiterhin als **Rückfall**: kann ein Gerät nicht umrechnen,
 * geht die Datei unverändert hinaus, und dann entscheidet wieder die Foto-App.
 */

import { api } from '../api/client'
import type { Media, Settings } from '../api/types'
import { aufnahmeKopf } from './images'
import type { Aufnahme } from './exif'
import { aufnahmeAusVideo } from './videoOrt'
import { groesse } from './format'
import { notiere } from './sendeprotokoll'
import { schwarz, verkleinereVideo, type Wandelergebnis, type Wandelziel } from './videowandeln'
import { WORTLAUT } from '../texte'
import type { Uhr } from './ortszeit'

export interface VideoInfo {
  width: number
  height: number
  durationMs: number
}

/**
 * Die Vorgaben, solange die Einstellungen noch unterwegs sind.
 *
 * Wie `MAX_EDGE` und `QUALITY` bei den Fotos: die geltenden Werte kommen aus
 * `/api/settings` (`video_max_edge`, `video_max_fps`) und lassen sich im
 * Betrieb ändern, ohne die Oberfläche neu zu bauen.
 */
export const VIDEO_MAX_EDGE = 1920
export const VIDEO_MAX_FPS = 30

export interface Videoupload {
  /**
   * Das Standbild ist da -- als Vorschau für die Kachel.
   *
   * **Gerechnet wird es hier drin, nicht beim Aufrufer** (#4). Vorher rief
   * jede der beiden Stellen selbst `grabPoster()` und reichte die Zusage
   * herein; damit standen Reihenfolge, Zeitpunkt und Fehlerbehandlung
   * zweimal da -- und liefen auseinander. Aus dem Betrieb: „bei jeder
   * Änderung in allen Bereichen die gleichen Änderungen durchführen." Der
   * sicherere Weg ist, dass es gar nicht zweimal dasteht.
   *
   * Der Rückruf kommt, sobald das Bild da ist, und blockiert nichts. Kommt
   * keines, kommt er nicht -- das ist ein Schönheitsfehler und kein Fehler.
   */
  onVorschau?: (bild: Blob) => void
  signal?: { abgebrochen: boolean }
  /** Zielgröße und Bildrate aus den Einstellungen. */
  ziel?: Partial<Wandelziel>
  /** Wie weit das Umrechnen ist -- **vor** dem Hochladen. */
  onRechnet?: (anteil: number) => void
  /**
   * Was aus dem Video wurde — **in jedem Fall**, auch wenn nichts gerechnet
   * wurde.
   *
   * Ein Rückruf und nicht zwei. Vorher gab es `onGrund` fürs Scheitern und
   * `onVerkleinert` fürs Gelingen, und die Kachel zog die Grenze nur im
   * zweiten nach — ein Video, das **nicht** umgerechnet werden konnte, behielt
   * damit die Warnung aus der Originalgröße, ein umgerechnetes bekam eine
   * neue. Zwei Wege für dieselbe Frage laufen auseinander; hier sind sie
   * einer.
   */
  onErgebnis?: (befund: Wandelergebnis) => void
  /** Die Uhr der Reise -- für Aufnahmezeiten ohne Zonenangabe. */
  reiseVersatz?: Uhr
  /**
   * The poster that came late (1.18.0): when it goes up after the video, the
   * caller gets the medium once more, now with its `poster_url`. A form
   * that keeps the medium in hand replaces it; the chat needs nothing -- its
   * next fetch of the list carries the poster anyway.
   */
  onStandbild?: (media: Media) => void
}

export interface AngemeldetesVideo {
  id: number
  received_bytes: number
  chunk_bytes: number
}

export function isVideo(file: File): boolean {
  return file.type.startsWith('video/')
}

/**
 * Dauer und Maße -- gelesen, nicht geraten.
 *
 * Gelesen wird aus der Datei auf dem Gerät, nicht über das Netz -- was
 * `ladeVideo()` dafür vorlädt, steht dort und ist dort begründet.
 */
export async function readVideoInfo(file: File): Promise<VideoInfo> {
  const [video, url] = await ladeVideo(file)
  try {
    return {
      width: video.videoWidth,
      height: video.videoHeight,
      // Manche Aufnahmen melden `Infinity`, bis einmal gespult wurde.
      durationMs: Number.isFinite(video.duration) ? Math.round(video.duration * 1000) : 0,
    }
  } finally {
    freigeben(video, url)
  }
}

/**
 * Das Standbild: der erste Augenblick als JPEG.
 *
 * Nicht Bild null, sondern eine Zehntelsekunde später — der allererste
 * Bildpunkt einer Aufnahme ist oft schwarz, weil die Kamera noch belichtet.
 *
 * Schlägt es fehl, ist das kein Fehler: dann bleibt das Video eben ohne
 * Vorschaubild. Ein Upload daran scheitern zu lassen wäre unverhältnismäßig.
 */
/**
 * **Nicht mehr exportiert** (#4): das Standbild macht `uploadVideo()` selbst.
 * Vorher rief es jede der beiden Stellen im Bogen und in der Eingabezeile
 * einzeln -- zwei Kopien derselben Reihenfolge.
 */
async function grabPoster(file: File): Promise<Blob | null> {
  /*
   * **Kein Wettlauf mehr um das Ganze** (#4).
   *
   * Hier stand `mitFrist(standbild(file), 15000)`, und das war ein Fehler mit
   * Ansage: `mitFrist` ist ein Wettlauf. Laeuft die Zeit ab, bekommt der
   * Aufrufer `null` -- die Arbeit dahinter **laeuft weiter** und haelt ihr
   * `<video>`-Element, bis sie ihr `finally` erreicht. Solange dieses Element
   * gar nichts dekodierte, kostete das nichts. Seit es im Dokument steht und
   * wirklich dekodiert, haelt es einen **Dekodierer** -- und der fehlt dann
   * dem naechsten Video beim Umrechnen.
   *
   * Gebraucht wird der Wettlauf nicht mehr: jeder Schritt darin hat seine
   * eigene Zeitgrenze (Laden 5 s, zweimal Warten 2,5 und 2 s, Spulen 3 s).
   * `standbild()` kommt also immer zum Ende und gibt sein Element immer frei.
   * Eine zweite Grenze darueber kann von hier an nur noch schaden.
   */
  return standbild(file).catch((fehler: unknown) => {
    // **Der Grund gehoert in den Mitschrieb** (#4). Aus dem Betrieb stand
    // dort dreimal „Video ohne Standbild" und kein Wort dazu, woran es lag --
    // und damit war die Meldung „kein Vorschaubild" nicht auswertbar.
    notiere(`Standbild fehlt: ${fehler instanceof Error ? fehler.message : String(fehler)}`)
    return null
  })
}

async function standbild(file: File): Promise<Blob | null> {
  const [video, url] = await ladeVideo(file)
  try {
    // Ein Bild muss erst einmal dekodiert sein -- `readyState >= 2`. Reicht
    // das Laden allein nicht, hilft ein kurzer stummer Anlauf; erlaubt ist er,
    // weil das Element stumm und `playsInline` ist, denn ohne beides lehnt
    // iOS ihn ohne Zutun ab.
    //
    // Gewartet wird auf den **Zustand**, nicht auf ein Ereignis. Ein Ereignis,
    // das schon vorbei ist, kommt nicht wieder: wer erst danach zuhört, wartet
    // für immer. Genau daran ist der erste Anlauf dieser Änderung
    // gescheitert -- und dieselbe Falle erklärt gut, warum es auf einem
    // langsameren Gerät anders ausgeht als hier.
    await warteBis(() => video.readyState >= 2, 2500)
    if (video.readyState < 2) {
      await video.play().catch(() => undefined)
      await warteBis(() => video.readyState >= 2, 2000)
      video.pause()
    }
    if (video.readyState < 2) {
      throw new Error(`nichts dekodiert (readyState ${video.readyState})`)
    }

    // Der allererste Bildpunkt einer Aufnahme ist oft schwarz, weil die
    // Kamera noch belichtet -- deshalb eine Zehntelsekunde später. Nur wenn
    // es dort überhaupt etwas gibt: meldet eine Aufnahme ihre Länge als
    // `Infinity` (kommt vor, bis einmal gespult wurde), führte das Warten auf
    // `seeked` ins Leere.
    let gespult = 'nicht noetig'
    if (Number.isFinite(video.duration) && video.duration > 0.2) {
      gespult = await spuleZu(video, 0.1).then(
        () => 'ja',
        (fehler: unknown) => (fehler instanceof Error ? fehler.message : 'nein'),
      )
    }

    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    if (canvas.width === 0 || canvas.height === 0) {
      throw new Error('das Element meldet keine Masse')
    }

    const context = canvas.getContext('2d')
    if (context === null) throw new Error('keine Leinwand zu bekommen')
    context.drawImage(video, 0, 0, canvas.width, canvas.height)

    // Hat der Browser überhaupt etwas dekodiert? Wenn nicht, bleibt die
    // Leinwand schwarz -- und ein schwarzes Rechteck als Standbild ist
    // schlechter als keins, denn dafür gibt es im Player einen Ausweg.
    //
    // Geprüft wird auf **schwarz**, nicht auf „überall dieselbe Farbe": ein
    // Bild darf einfarbig sein. Eine weiße Wand, Nebel, ein Nachthimmel --
    // alles gültige Standbilder, und sie wegzuwerfen wäre schlimmer als der
    // Fehler, den diese Prüfung sucht.
    if (schwarz(context, canvas.width, canvas.height)) {
      // Die Zahlen daneben, weil sie den Fall trennen: bei `spulen=ja` hat
      // der Browser gespult und trotzdem nichts gemalt, bei `spulen=…` lag
      // es am Spulen.
      throw new Error(`nur Schwarz gemalt (spulen: ${gespult}, ${canvas.width}x${canvas.height})`)
    }

    return await new Promise<Blob | null>((fertig) =>
      canvas.toBlob((blob) => fertig(blob), 'image/jpeg', 0.75),
    )
  } finally {
    freigeben(video, url)
  }
}

/**
 * Nach dieser Zeit ist Schluss.
 *
 * Ein Standbild, das nicht kommt, darf nichts aufhalten. Auf iOS kann das
 * Warten auf ein Ereignis ins Leere laufen -- ohne Frist stünde der Upload
 * dann für immer.
 */
/**
 * Wie lange auf ein `seeked` gewartet wird.
 *
 * Dieselbe Groessenordnung wie beim Laden. Wer nach drei Sekunden nicht an
 * einer Zehntelsekunde angekommen ist, kommt dort nicht mehr an.
 */
const SPULFRIST_MS = 3000

/*
 * **Hier stand `mitFrist` -- ein Wettlauf um eine ganze Arbeit.**
 *
 * Es ist weg, weil sein letzter Aufrufer weg ist, und der Grund gehoert
 * aufgehoben: `Promise.race` ist **kein Abbruch**. Laeuft die Zeit ab,
 * bekommt der Aufrufer eine Antwort -- die Arbeit dahinter laeuft weiter und
 * haelt, was sie hat. Ueber dem Standbild war genau das der Schaden: das
 * `<video>`-Element blieb liegen, und mit ihm ein Dekodierer, von denen ein
 * iPhone wenige hat.
 *
 * Wer eine Arbeit wirklich begrenzen will, begrenzt **ihre Schritte** --
 * jeder mit seiner eigenen Zeitgrenze, jeder mit seinem `finally`. Dann
 * kommt sie immer zum Ende, und eine Grenze darueber braucht es nicht.
 */

/**
 * Wartet, bis eine Bedingung zutrifft -- oder bis die Zeit um ist.
 *
 * Nachgesehen statt zugehört: ein Ereignis, das vor dem Zuhören eingetreten
 * ist, kommt nicht wieder. Zwanzig Millisekunden Takt sind grob genug, dass
 * es nichts kostet, und fein genug, dass man es nicht merkt.
 */
function warteBis(bedingung: () => boolean, ms: number): Promise<void> {
  if (bedingung()) return Promise.resolve()

  return new Promise<void>((fertig) => {
    const bis = Date.now() + ms
    const takt = setInterval(() => {
      if (bedingung() || Date.now() > bis) {
        clearInterval(takt)
        fertig()
      }
    }, 20)
  })
}

/**
 * The target of a video from the settings -- one place for the chat and the
 * point (1.101.0, review). Both callers wrote the same three fields out;
 * the next setting would have reached one of them.
 */
export function videoZiel(werte: Settings | null): Partial<Wandelziel> | undefined {
  if (werte === null) return undefined
  return {
    maxEdge: werte.video_max_edge,
    maxFps: werte.video_max_fps,
    // Nur die beiden erlaubten Werte -- was der Server durchlaesst, ist
    // geprueft, aber der Typ hier ist enger als `string`.
    latenz: werte.video_latency === 'realtime' ? 'realtime' : 'quality',
  }
}

/**
 * Lädt die Datei in Stücken hoch.
 *
 * Zwei Dinge machen das anders als bei einem Bild:
 *
 * 1. **Der Server sagt, wie groß ein Stück sein darf.** Nicht der Client:
 *    was durchpasst, hängt an `post_max_size` des Pakets, und das weiß nur
 *    die andere Seite.
 * 2. **Nach einem Abbruch wird gefragt, nicht geraten.** Der Stand steht auf
 *    dem Server; der Browser holt ihn und macht dort weiter. Blind noch
 *    einmal zu senden schriebe die Datei doppelt.
 * 3. **Vorher wird verkleinert** (#4). Hochgeladen wird also nicht unbedingt
 *    die Datei, die jemand ausgewählt hat -- deshalb heisst der Parameter
 *    `gewaehlt` und die Datei danach `file`. Ort und Zeit werden **vor** dem
 *    Umrechnen aus dem Original gelesen und gehen als Kopfzeilen mit.
 */
export async function uploadVideo(
  tripId: number,
  gewaehlt: File,
  onProgress: (anteil: number) => void,
  optionen: Videoupload = {},
): Promise<Media> {
  const {
    onVorschau,
    signal,
    ziel,
    onRechnet,
    onErgebnis,
    reiseVersatz = null,
    onStandbild,
  } = optionen

  // **Zuerst lesen, dann rechnen.** Ein iPhone legt Ort und Zeit im
  // `udta`-Atom ab; ein neu geschriebenes MP4 hat das nicht mehr. Was hier
  // nicht gelesen wird, ist danach weg -- deshalb steht es vor dem Umrechnen
  // und nicht daneben.
  const aufnahme = await aufnahmeAusVideo(gewaehlt, reiseVersatz).catch(() => null)

  /*
   * **Alles durchreichen, nicht zwei Felder abschreiben.**
   *
   * Hier stand `{ maxEdge, maxFps }` -- eine Liste von Hand, und genau eine
   * Angabe zu wenig: `latenz` kam aus den Einstellungen bis in beide
   * Aufrufer und **hier** nicht mehr weiter. Die Einstellung war damit ein
   * Knopf ohne Draht: gespeichert, geprueft, angezeigt -- und wirkungslos.
   *
   * Ausgeschrieben bleiben nur die zwei mit einer **Vorgabe**; alles Weitere
   * geht, wie es hereinkam. Die naechste Angabe braucht dann keine Zeile
   * mehr, und das ist der Punkt: eine Liste, die man pflegen muss, wird
   * einmal nicht gepflegt.
   */
  const ergebnis = await verkleinereVideo(
    gewaehlt,
    {
      ...ziel,
      maxEdge: ziel?.maxEdge ?? VIDEO_MAX_EDGE,
      maxFps: ziel?.maxFps ?? VIDEO_MAX_FPS,
    },
    onRechnet,
    signal,
  )
  // Keine stille Absage und keine stille Zusage: was herauskam, geht immer
  // hinaus -- die Kachel leitet daraus Grösse, Grenze, Ausbeute und Hinweis ab.
  onErgebnis?.(ergebnis)

  /*
   * **Was nicht überall läuft, geht nicht hinaus** (#41).
   *
   * Aus dem Betrieb: „egal welches Ausgangsformat das Bild/Video hat (HEVC,
   * HEIC, …) → es landet IMMER in einem kompatiblen Format (iOS, Windows,
   * Android)." Genau das ist hier die Grenze: konnte der Browser nicht nach
   * H.264 rechnen, läge sonst ein Video auf dem Server, das ein Teil der
   * Mitlesenden nicht abspielen kann -- und niemand sähe der Kachel an,
   * woran es liegt.
   *
   * **Der Befund steht schon vor dem Wurf.** `onErgebnis` ist eine Zeile
   * weiter oben: die Kachel zeigt Größe und Grund, und erst danach bricht es
   * ab. Umgekehrt stünde eine Fehlermeldung ohne die Zahlen daneben.
   *
   * **Der Preis gehört genannt:** auf einem Browser ohne WebCodecs geht damit
   * gar kein Video mehr. Das ist die Entscheidung aus #41 -- lieber eine
   * klare Absage als ein Beitrag, den die Hälfte nicht sehen kann.
   */
  if (!ergebnis.kompatibel) {
    // **Ein Satz, und er faengt gross an und hoert mit einem Punkt auf.**
    // Aus dem Betrieb, am Bildschirmfoto: „Decoder failure Ohne Umrechnung
    // wuerde es auf manchen Geraeten nicht abspielen." Der Grund kommt vom
    // Browser, auf Englisch und ohne Satzzeichen -- angeklebt wurde daraus
    // ein Satz, der wie ein Fehler im Text aussieht. Er gehoert in Klammern
    // hinter den deutschen Satz, wo ein Fremdwort hingehoert.
    const gemeldet = ergebnis.grund.trim().replace(/[.\s]+$/, '')
    throw new Error(
      gemeldet === ''
        ? WORTLAUT.technik.video.keinFormat
        : WORTLAUT.technik.video.nichtUmgerechnetMitGrund(gemeldet),
    )
  }

  const file = ergebnis.datei

  /*
   * **Das Standbild erst jetzt, und aus der fertigen Datei** (#4).
   *
   * Vorher lief es beim Aufrufer los, **bevor** `verkleinereVideo()` anfing --
   * also gleichzeitig. Beide brauchen einen Dekodierer, und iOS gibt nur
   * wenige her; wer sich einen mit dem Umrechner teilt, wartet auf einen, den
   * es gerade nicht gibt. Hier laeuft es hinterher und dafuer parallel zum
   * Hochladen der Stuecke -- das kostet Leitung, keinen Dekodierer.
   *
   * **Und aus `ergebnis.datei`, nicht aus dem Original.** Was hier liegt, ist
   * H.264: das kann jeder Browser lesen. Aus einer HEVC-Aufnahme haette ein
   * Chrome nie ein Standbild bekommen.
   *
   * Der Rueckruf geht sofort hinaus, gewartet wird erst ganz unten.
   */
  /*
   * **Der kurze Weg zuerst** (#4): das Bild ist beim Umrechnen abgefallen.
   *
   * Aus dem Betrieb, dreimal gemessen und zweimal reproduziert: auf einem
   * iPhone meldet ein `<video>`-Element **fuer eine frisch gerechnete Datei
   * ueberhaupt nicht** -- auch nicht in zwanzig Sekunden, und auch nicht bei
   * kleiner Last: gemessen an einer 16-Sekunden-Umrechnung mit 11 MB Ergebnis. Auf einem iPad schon --
   * es rechnet 55 Bilder je Sekunde statt 26. Noch mehr Zeit waere der dritte
   * Versuch derselben Sache gewesen; der Fehler steckte im Ansatz, naemlich
   * ein zweites Mal zu dekodieren.
   *
   * Liegt hier ein Bild, ist alles davon erspart: kein Element, keine
   * Blob-Adresse, kein Dekodierer, kein Warten. Der Rueckruf geht sofort
   * hinaus.
   *
   * **Hier stand, `grabPoster()` bleibe fuer den Fall `schon-gut`** -- dort
   * werde nichts gerechnet, also falle auch nichts ab, „das sind Dateien
   * unter fuenfundzwanzig Megabyte, dort hat es nie geklemmt". Das war eine
   * Vermutung, und eine Messung hat sie widerlegt: eine Aufnahme direkt aus
   * der Kamera, 480x360 und 2,3 MB, geht unveraendert hinaus -- und das
   * Element meldete nach zwanzig Sekunden „Dieses Video meldet seine Masse
   * nicht". Kein Vorschaubild.
   *
   * Seitdem holt `standbildAusQuelle()` auch dort ein Bild, mit Zerleger und
   * Dekodierer statt mit einem Element. `grabPoster()` bleibt nur noch fuer
   * den Fall, dass **auch das** nichts hergibt.
   */
  const vorschau = ergebnis.standbild !== null
    ? Promise.resolve(ergebnis.standbild).then((bild) => {
        // Woher es kommt, gehoert dazu: bei einem unveraenderten Video ist
        // nichts umgerechnet worden, und „aus der Umrechnung" waere schlicht
        // gelogen.
        notiere(
          `Standbild ${ergebnis.verkleinert ? 'aus der Umrechnung' : 'aus der Quelle'}`
          + `: ${groesse(bild.size)}`,
        )
        onVorschau?.(bild)
        return bild
      })
    : grabPoster(file)
      .then((bild) => {
        if (bild !== null) onVorschau?.(bild)
        return bild
      })
      .catch(() => null)

  return transferVideo(
    tripId,
    file,
    ergebnis.breite > 0 && ergebnis.hoehe > 0 && ergebnis.dauerMs > 0
      ? { width: ergebnis.breite, height: ergebnis.hoehe, durationMs: ergebnis.dauerMs }
      : null,
    vorschau,
    onProgress,
    { signal, aufnahme, onStandbild },
  )
}


/**
 * The transfer of a finished video (#90, 1.96.0): announce, the pieces,
 * complete, the still -- the second half of `uploadVideo()`, taken out so
 * that a video made in the browser (the animated day card) goes the same
 * way and not a second one. What comes in here is already converted and
 * within the limits; the conversion is the caller's.
 */
export async function transferVideo(
  tripId: number,
  file: File,
  known: { width: number; height: number; durationMs: number } | null,
  poster: Promise<Blob | null>,
  onProgress: (share: number) => void,
  options: {
    signal?: { abgebrochen: boolean }
    aufnahme?: Aufnahme | null
    onStandbild?: (media: Media) => void
    /** The id as soon as the server knows it -- so that a cancel can discard it (#90). */
    onRegistered?: (id: number) => void
  } = {},
): Promise<Media> {
  const { signal, aufnahme = null, onStandbild, onRegistered } = options
  const angemeldet = await api.post<AngemeldetesVideo>(
    `/api/trips/${tripId}/media/video`,
    { bytes: file.size },
    undefined,
    // Dieselben Kopfzeilen wie bei einem Bild -- eine Regel, eine Stelle.
    aufnahmeKopf(aufnahme ?? undefined),
  )

  onRegistered?.(angemeldet.id)
  let stand = angemeldet.received_bytes
  const stueck = Math.max(64 * 1024, angemeldet.chunk_bytes)
  /*
   * **Jeder Schritt wird notiert** (#4).
   *
   * Aus dem Betrieb: „es läuft einmal auf 100 % und bleibt dann stehen." Der
   * Balken sagt nur, wie viele Bytes draußen sind -- er sagt nichts über die
   * drei Schritte danach. Steht im Mitschrieb „alle Stücke draußen" ohne
   * „abgeschlossen", weiß man auf Anhieb, wo es hängt.
   */
  notiere(
    `Video angemeldet: ${groesse(file.size)} als ${angemeldet.id}`
    + `, Stücke zu ${groesse(stueck)}`
    + (stand > 0 ? `, weiter ab ${groesse(stand)}` : ''),
  )

  while (stand < file.size) {
    if (signal?.abgebrochen) throw new Error(WORTLAUT.technik.fehler.hochladenAbgebrochen)

    const ende = Math.min(stand + stueck, file.size)
    const teil = file.slice(stand, ende)
    const range = `bytes ${stand}-${ende - 1}/${file.size}`

    try {
      const antwort = await api.uploadChunk<{ received_bytes: number }>(
        `/api/media/${angemeldet.id}/bytes`,
        teil,
        range,
        (anteil) => onProgress((stand + anteil * teil.size) / file.size),
      )
      stand = antwort.received_bytes
    } catch (fehler) {
      // Beim Abbruch mitten im Stück weiß der Browser nicht, wie viel
      // angekommen ist. Fragen statt raten -- und wenn der Server auch nicht
      // weiterweiß, ist es ein echter Fehler.
      const jetzt = await api
        .get<{ received_bytes: number }>(`/api/media/${angemeldet.id}/upload`)
        .catch(() => null)
      if (jetzt === null || jetzt.received_bytes <= stand) throw fehler
      stand = jetzt.received_bytes
    }

    onProgress(stand / file.size)
  }

  notiere(`Video: alle Stücke draußen (${groesse(file.size)})`)

  /*
   * **Nicht fragen, was schon dasteht** (#4).
   *
   * Der Zerleger kennt Maße und Dauer aus den Verwaltungsdaten der Datei --
   * er hat sie gerade gelesen. Hier stand trotzdem ein `<video>`-Element, das
   * dasselbe noch einmal ermittelt: der Schritt **nach** dem letzten Stück und
   * **vor** `/complete`, also genau dort, wo der Balken auf 100 % steht. Blieb
   * er hängen, blieb der Upload hängen.
   *
   * Solange die Zahlen bekannt sind, wird dieser Weg jetzt gar nicht mehr
   * betreten. Und wenn er doch betreten wird, wartet `ladeVideo()` hoechstens
   * fuenf Sekunden und bricht dann ab. Zwei Sperren, weil der erste nur den
   * Regelfall trifft.
   */
  const info = known !== null && known.width > 0 && known.height > 0 && known.durationMs > 0
    ? known
    : await readVideoInfo(file).catch(() => ({ width: 0, height: 0, durationMs: 0 }))
  // **Beide Antworten beschreiben das fertige Medium** -- samt Adresse,
  // Standbild und Dauer. Zurueck kommt deshalb das Medium und nicht nur seine
  // Nummer: das Formular an der Karte braucht es fuer die Kachel, und es gibt
  // keinen Weg, ein einzelnes Medium nachzufragen -- `/api/media/{id}` kennt
  // nur DELETE. Es im Browser zusammenzureimen hiesse, die Adressen des
  // Servers ein zweites Mal zu schreiben.
  const fertig = await api.post<Media>(`/api/media/${angemeldet.id}/complete`, {
    width: info.width,
    height: info.height,
    duration_ms: info.durationMs,
  })

  notiere(`Video abgeschlossen: ${fertig.id} · ${info.width}x${info.height} · ${info.durationMs} ms`)

  /*
   * **Auf das Standbild wird nicht mehr gewartet** (#4).
   *
   * Zwei Dinge treffen sich hier, und beide zeigen in dieselbe Richtung.
   * Erstens stand hier einmal ein zweiter `grabPoster()`-Aufruf als Rueckfall
   * -- ein weiteres Element, eine weitere Wartezeit, und zwar **bei 100 %**:
   * genau dort, wo es fuer den Benutzer wie ein Stehenbleiben aussieht.
   * Zweitens darf das Warten auf die Masse jetzt zwanzig Sekunden dauern
   * statt fuenf, weil ein beschaeftigtes Geraet sie nicht schneller meldet.
   *
   * Beides zusammen waere ein Balken, der auf 100 % steht und nichts tut. Der
   * Beitrag ist an dieser Stelle aber fertig: `/complete` ist durch, die Datei
   * liegt vollstaendig auf dem Server. Was noch fehlt, ist ein Vorschaubild --
   * und **darauf wartet niemand.**
   *
   * Ist es rechtzeitig da, geht es gleich mit hinaus und die Antwort traegt
   * seine Adresse. Ist es das nicht, geht es **hinterher** hinaus, im Stillen;
   * der naechste Abruf der Nachrichtenliste nimmt es mit. Aufgegeben wird es
   * nicht -- `standbild()` laeuft zu Ende und gibt sein Element frei, und
   * genau deshalb darf man es hier loslassen.
   */
  const GEDULD_MS = 1500
  const standbild = await Promise.race([
    poster,
    new Promise<'spaeter'>((f) => setTimeout(() => f('spaeter'), GEDULD_MS)),
  ])

  if (standbild === 'spaeter') {
    notiere('Standbild kommt nach -- der Beitrag wartet nicht darauf')
    void poster.then((bild) => {
      if (bild === null) return
      notiere(`Standbild nachgereicht: ${groesse(bild.size)}`)
      void api
        .upload<Media>(`/api/media/${angemeldet.id}/poster`, bild)
        .then((mitBild) => onStandbild?.(mitBild))
        .catch(() => undefined)
    })
    return fertig
  }

  notiere(standbild === null ? 'Video ohne Standbild' : `Standbild: ${groesse(standbild.size)}`)
  if (standbild === null) return fertig

  // Ein fehlendes Standbild ist ein Schoenheitsfehler, kein Grund, den
  // fertigen Upload zu verwerfen -- dann gilt eben die Antwort von vorhin.
  return await api
    .upload<Media>(`/api/media/${angemeldet.id}/poster`, standbild)
    .catch(() => fertig)
}

/**
 * Where a medium's small picture lives (1.18.0): the thumbnail of a photo,
 * the poster of a video -- and for a video **whose poster is not there
 * yet**, the address it will have. The server builds `url` and `poster_url`
 * alike (`/api/media/{id}/file`, `/api/media/{id}/poster`); deriving the
 * one from the other keeps every card asking at the right door, and
 * `Standbild` asks again until the poster has arrived.
 */
export function standbildAdresse(media: Pick<Media, 'kind' | 'url' | 'poster_url' | 'thumb_url'>): string | null {
  if (media.kind === 'video') return media.poster_url ?? media.url.replace(/\/file$/, '/poster')
  if (media.kind === 'image') return media.thumb_url ?? media.url
  return null
}

/** Ob dieser Browser den Typ überhaupt abspielen kann. */
export function kannAbspielen(mime: string): boolean {
  const probe = document.createElement('video')
  return probe.canPlayType(mime) !== ''
}

/** `4500` → `0:04`. */
export function dauerText(ms: number): string {
  const sekunden = Math.round(ms / 1000)
  return `${Math.floor(sekunden / 60)}:${String(sekunden % 60).padStart(2, '0')}`
}

/**
 * Nach dieser Zeit gilt ein `<video>`-Element als tot.
 *
 * **Hier standen fuenf Sekunden, und die Begruendung war falsch** (#4). Sie
 * lautete: „wer laenger als das braucht, um die Masse zu melden, meldet sie
 * gar nicht mehr." Aus dem Betrieb kam die Gegenrede als Messung -- zweimal
 * hintereinander, an zwei verschiedenen Videos:
 *
 *     22:01:22 · Standbild fehlt: Dieses Video meldet seine Masse nicht.
 *     22:03:24 · Standbild fehlt: Dieses Video meldet seine Masse nicht.
 *
 * Das ist genau diese Zeitgrenze, und der Zeitpunkt sagt alles: sie lief
 * **vier Sekunden nach dem Ende eines zweiundachtzig Sekunden langen
 * Umrechnens** ab, waehrend die ersten Stuecke schon hinausgingen. Ein Geraet,
 * das gerade 2743 Bilder umgerechnet hat und nebenher 57 MB hochlaedt, meldet
 * die Masse einer 57-MB-Datei nicht in fuenf Sekunden -- es ist beschaeftigt,
 * nicht kaputt.
 *
 * Zwanzig Sekunden kosten nichts: das Standbild laeuft neben dem Hochladen,
 * und niemand wartet darauf (siehe `uploadVideo()`).
 *
 * **Aber auch zwanzig haben nicht gereicht, und die Erklaerung war zu eng.**
 * Aus dem Betrieb kam derselbe Satz noch einmal -- nach einer Umrechnung von
 * **sechzehn** Sekunden, mit einer Ausgabedatei von elf Megabyte:
 *
 *     00:12:31 · Video gerechnet: 47 MB → 11 MB in 16.4s
 *     00:12:51 · Standbild fehlt: Dieses Video meldet seine Masse nicht.
 *
 * Wieder exakt diese Grenze, bei kleiner Last und kleiner Datei. „Beschaeftigt,
 * nicht kaputt" traegt das nicht mehr: auf diesem Geraet meldet ein
 * `<video>`-Element fuer eine frisch gerechnete Datei **gar keine Masse**.
 *
 * Deshalb bleibt diese Zahl, wie sie ist, und es kommen keine dreissig
 * Sekunden dazu -- das waere der vierte Versuch derselben Sache. Der Weg fuehrt
 * ueber das Standbild aus der Umrechnung (`videowandeln.ts`), und diese Grenze
 * traegt nur noch den Fall „schon gut", in dem nichts gerechnet wurde.
 */
const LADEFRIST_MS = 20000

/**
 * Ein `<video>`-Element mit geladener Auskunft -- oder eine Absage.
 *
 * **Hier hing der Upload** (#4). Aus dem Betrieb: „nicht an allen Geräten und
 * nicht immer: es läuft einmal auf 100 % und bleibt dann stehen." An dieser
 * Stelle stand ein Versprechen ohne Frist:
 *
 * ```ts
 * video.onloadedmetadata = () => fertig()
 * video.onerror = () => fehler(…)
 * ```
 *
 * Kommt keines von beiden, wird es nie eingelöst. Und der Aufrufer sitzt
 * **nach** dem letzten Stück und **vor** `/complete` -- der Balken steht also
 * längst auf 100 %, während nichts mehr passiert. Ein `.catch()` daneben hilft
 * dagegen nicht: Hängen ist keine Absage.
 *
 * **Warum es überhaupt ausbleibt**, und warum nur manchmal: iOS lässt nur
 * wenige Medienelemente gleichzeitig dekodieren. Wer seine alten nicht
 * freigibt, bekommt für das nächste keinen Dekodierer -- und dann kommt weder
 * `loadedmetadata` noch `error`. Deshalb steht unten `freigeben()`, und
 * deshalb ruft es jeder Aufrufer im `finally`.
 */
async function ladeVideo(file: File): Promise<[HTMLVideoElement, string]> {
  const url = URL.createObjectURL(file)
  const video = document.createElement('video')
  // `auto` statt `metadata`: für ein Standbild braucht es ein dekodiertes
  // Bild, nicht nur die Maße. Die Datei liegt ohnehin auf dem Gerät.
  video.preload = 'auto'
  video.muted = true
  // Ohne das öffnet iOS den Vollbildplayer, statt im Hintergrund zu laden.
  video.playsInline = true
  video.src = url

  /*
   * **Hier wurde das Element ins Dokument gehaengt, und es ist wieder heraus.**
   *
   * Die Begruendung war: „iOS dekodiert fuer ein losgeloestes
   * `<video>`-Element nichts", abgeleitet daraus, dass in jedem Mitschrieb
   * „Video ohne Standbild" stand. Der Schluss war falsch, und ein iPad hat es
   * widerlegt: **auf derselben Version, ohne eingehaengtes Element, kamen dort
   * zwei Standbilder** (338 kB und 331 kB). Ein losgeloestes Element
   * dekodiert also sehr wohl.
   *
   * Was wirklich fehlte, war Zeit. Dasselbe iPhone meldete
   * „Dieses Video meldet seine Maße nicht" -- die Zeitgrenze fuer
   * `loadedmetadata`, vier Sekunden nach einem zweiundachtzig Sekunden langen
   * Umrechnen. Das iPad rechnet 53 Bilder je Sekunde, das iPhone 33; das eine
   * war rechtzeitig fertig, das andere nicht. Die Grenze steht deshalb bei
   * zwanzig Sekunden (`LADEFRIST_MS`).
   *
   * **Und weshalb es nicht einfach stehen bleibt**, obwohl es nicht stoerte:
   * ein Element im Dokument, das wirklich dekodiert, belegt einen
   * Dekodierer -- und genau deren Knappheit steht hinter „Decoder failure".
   * Eine Vermutung, gegen die eine Messung spricht, mit einem Preis, den man
   * kennt: die kommt heraus.
   */
  try {
    await new Promise<void>((fertig, fehler) => {
      const uhr = setTimeout(
        () => fehler(new Error(WORTLAUT.technik.video.keineMasse)),
        LADEFRIST_MS,
      )
      video.onloadedmetadata = () => {
        clearTimeout(uhr)
        fertig()
      }
      video.onerror = () => {
        clearTimeout(uhr)
        fehler(new Error(WORTLAUT.technik.video.nichtOeffnen))
      }
    })
  } catch (fehler) {
    // Auch die Absage gibt frei -- sonst bliebe genau das Element liegen, das
    // dem nächsten den Dekodierer wegnimmt.
    freigeben(video, url)
    throw fehler
  }

  return [video, url]
}

/**
 * Das Element wirklich loslassen.
 *
 * `URL.revokeObjectURL` allein reicht nicht: solange `src` gesetzt ist, hält
 * der Browser den Dekodierer. Erst `src = ''` und ein `load()` geben ihn her.
 */
function freigeben(video: HTMLVideoElement, url: string): void {
  URL.revokeObjectURL(url)
  video.onloadedmetadata = null
  video.onerror = null
  video.removeAttribute('src')
  try {
    video.load()
  } catch {
    // Ein Browser, der das nicht mag, hat es auch nicht nötig.
  }
  // `remove()` schadet auch dann nicht, wenn das Element nie im Dokument
  // stand -- und es ist die Sperre dagegen, dass ein spaeterer Umbau es
  // wieder hineinstellt und das Wegraeumen vergisst.
  video.remove()
}

/**
 * **Ausgefuehrt exportiert, damit die Frist messbar ist.**
 *
 * Der Browserlauf kann sie nicht pruefen: das Chromium des Pruefstands hat
 * keinen H.264-Dekodierer, `<video>` lehnt die Pruefdatei mit
 * `DEMUXER_ERROR_NO_SUPPORTED_STREAMS` ab, und damit wird diese Stelle nie
 * erreicht. Eine Gegenprobe, die den Zweig gar nicht betritt, bleibt gruen
 * und beweist nichts -- das ist beim ersten Anlauf genau so passiert.
 *
 * Gebraucht wird hier nur, was ein `<video>` an dieser Stelle hergibt:
 * `duration`, `currentTime` und die beiden Zuhoerer. Ein Doppel davon
 * reicht, und dann laesst sich die Frist mit der Uhr messen.
 */
export function spuleZu(video: HTMLVideoElement, sekunde: number): Promise<void> {
  const ziel = Math.min(sekunde, Math.max(0, video.duration - 0.05))
  if (!Number.isFinite(ziel)) {
    return Promise.reject(new Error(WORTLAUT.technik.video.nichtSpulbar))
  }

  return new Promise<void>((fertig, fehler) => {
    /*
     * **Hier stand der Zwilling des Fehlers aus #4.** `ladeVideo()` bekam eine
     * Frist, diese Stelle nicht -- und sie wartet auf dieselbe Art von
     * Ereignis, das auf iOS ausbleiben kann.
     *
     * Der Schaden war schlimmer als ein langsames Standbild: bleibt dieses
     * Versprechen offen, erreicht `standbild()` sein `finally` nie, und das
     * `<video>`-Element **behaelt seinen Dekodierer**. Nach aussen sah das
     * harmlos aus, weil `grabPoster()` den Aufrufer nach fuenf Sekunden mit
     * `null` abspeist -- also „kein Vorschaubild", nicht „haengt". Der
     * Dekodierer war trotzdem weg, und iOS hat nur wenige. Beim naechsten
     * Video fehlte er dann, und das war die Meldung „bleibt oft haengen".
     *
     * Eine Ursache, drei Meldungen: kein Vorschaubild, kein Format, und der
     * Upload steht.
     */
    const uhr = setTimeout(
      () => fehler(new Error(WORTLAUT.technik.video.nichtGespult)),
      SPULFRIST_MS,
    )
    const fertigMachen = (): void => {
      clearTimeout(uhr)
      fertig()
    }
    video.addEventListener('seeked', fertigMachen, { once: true })
    video.addEventListener(
      'error',
      () => {
        clearTimeout(uhr)
        fehler(new Error(WORTLAUT.technik.video.spulenFehlgeschlagen))
      },
      { once: true },
    )
    video.currentTime = ziel
  })
}
